import { useMemo } from 'react';

import { q } from '@actual-app/core/shared/query';
import type { IntegerAmount } from '@actual-app/core/shared/util';
import { useQuery } from '@tanstack/react-query';

import { aqlQuery } from '#queries/aqlQuery';

import type { Categorias } from './calculos';
import { gastoPorCategoriaYMes, rangosHastaDia } from './gastosRosa';
import type { GastoPorMes } from './gastosRosa';

/**
 * UNA consulta agrupada por mes × categoría con lo gastado de cada mes del
 * día 1 al día `dia` (mismos filtros que `consultaMovimientos`: solo cuentas
 * del presupuesto y sin traspasos entre ellas).
 */
export function consultaHastaDia(meses: readonly string[], dia: number) {
  return q('transactions')
    .filter({
      'account.offbudget': false,
      $and: [
        {
          $or: [
            { 'payee.transfer_acct': null },
            { 'payee.transfer_acct.offbudget': true },
          ],
        },
        {
          $or: rangosHastaDia(meses, dia).map(r => ({
            // AQL solo aplica el primer operador de cada objeto.
            $and: [{ date: { $gte: r.desde } }, { date: { $lte: r.hasta } }],
          })),
        },
      ],
    })
    .groupBy([{ $month: '$date' }, { $id: '$category' }])
    .select([
      { date: { $month: '$date' } },
      { category: { $id: '$category.id' } },
      { amount: { $sum: '$amount' } },
    ]);
}

/**
 * Gasto por categoría de cada mes de `meses` hasta el día `dia` (para «lo
 * habitual hasta hoy» y la comparación a igual día). Sin las categorías
 * excluidas ni las ocultas. `null` si no está activo.
 */
export function useGastoHastaDia({
  meses,
  dia,
  activo,
  categorias,
  excluidas,
}: {
  meses: readonly string[];
  dia: number;
  activo: boolean;
  categorias: Categorias;
  excluidas: ReadonlySet<string>;
}): { datos: GastoPorMes | null; isLoading: boolean } {
  const { data, isLoading } = useQuery({
    queryKey: ['informes', 'hasta-dia', meses.join(','), dia],
    enabled: activo && meses.length > 0,
    staleTime: 30_000,
    queryFn: async () => {
      const { data: filas } = await aqlQuery(consultaHastaDia(meses, dia));
      return filas as Array<{
        date: string;
        category: string | null;
        amount: IntegerAmount;
      }>;
    },
  });
  const datos = useMemo(() => {
    if (!activo || !data) {
      return null;
    }
    const contables = data
      .filter(
        f =>
          f.category == null ||
          (!excluidas.has(f.category) && !categorias.get(f.category)?.oculta),
      )
      .map(f => ({ mes: f.date, categoria: f.category, importe: f.amount }));
    return gastoPorCategoriaYMes(contables, categorias);
  }, [activo, data, categorias, excluidas]);
  return { datos, isLoading: activo && isLoading };
}
